import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { CheckCircle2, Loader2, XCircle } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const [state, setState] = useState("checking");
  const sessionId = params.get("session_id");

  useEffect(() => {
    if (!sessionId) { setState("error"); return; }
    let tries = 0, stop = false;
    const poll = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { setState("paid"); refresh && refresh(); return; }
        if (["expired", "failed"].includes(data.payment_status)) { setState("error"); return; }
      } catch { /* retry */ }
      if (++tries < 6 && !stop) setTimeout(poll, 2000); else if (!stop) setState("pending");
    };
    poll();
    return () => { stop = true; };
  }, [sessionId]); // eslint-disable-line

  return (
    <div className="min-h-screen grid place-items-center bg-secondary/40 p-6">
      <div className="bg-card border border-border rounded-2xl p-8 max-w-md w-full text-center" data-testid="payment-result">
        {state === "checking" && <><Loader2 className="mx-auto animate-spin text-emerald-brand" size={40} /><p className="mt-4 font-semibold">Confirming your subscription…</p></>}
        {state === "paid" && <><CheckCircle2 className="mx-auto text-emerald-brand" size={48} /><h1 className="font-heading text-2xl font-bold mt-4">You're all set</h1><p className="text-sm text-muted-foreground mt-2">Your GetCerty plan is active. Reminders, Evidence Packs and email-in filing are running.</p></>}
        {state === "pending" && <><Loader2 className="mx-auto text-amber-500" size={40} /><h1 className="font-heading text-xl font-bold mt-4">Payment is being processed</h1><p className="text-sm text-muted-foreground mt-2">We'll activate your plan the moment Stripe confirms it. You can carry on using GetCerty.</p></>}
        {state === "error" && <><XCircle className="mx-auto text-rose-500" size={48} /><h1 className="font-heading text-xl font-bold mt-4">Something went wrong</h1><p className="text-sm text-muted-foreground mt-2">Your card wasn't charged. Please try again from Settings.</p></>}
        <Link to={state === "error" ? "/settings" : "/dashboard"} data-testid="payment-continue" className="mt-6 inline-flex min-h-[44px] px-5 items-center rounded-xl bg-navy text-white font-semibold">Continue</Link>
      </div>
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="min-h-screen grid place-items-center bg-secondary/40 p-6">
      <div className="bg-card border border-border rounded-2xl p-8 max-w-md w-full text-center" data-testid="payment-cancel">
        <XCircle className="mx-auto text-muted-foreground" size={44} />
        <h1 className="font-heading text-xl font-bold mt-4">Checkout cancelled</h1>
        <p className="text-sm text-muted-foreground mt-2">No charge was made. Your trial continues as normal.</p>
        <Link to="/settings" className="mt-6 inline-flex min-h-[44px] px-5 items-center rounded-xl bg-navy text-white font-semibold">Back to settings</Link>
      </div>
    </div>
  );
}
